import React, { useState, useEffect, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";

interface AuditEntry {
  id: number;
  siteId: number;
  action: string;
  changedBy: string;
  details: string;
  timestamp: string;
}

type SortKey = "timestamp" | "action" | "changedBy";
type SortDir = "asc" | "desc";

export default function IpBlockingAuditPanel() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const { subscription } = useSubscriptions();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("timestamp");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterAction, setFilterAction] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Admin app users are already authorized admins/managers
  const canManage = true;

  const fetchData = useCallback(async () => {
    try {
      const res = await api.messaging.get("BlockedIp/audit");
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, toastAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredEntries = useMemo(() => {
    let result = [...entries];

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.details.toLowerCase().includes(lower) ||
          e.changedBy.toLowerCase().includes(lower) ||
          e.action.toLowerCase().includes(lower)
      );
    }

    if (filterAction !== "all") {
      result = result.filter((e) => e.action === filterAction);
    }

    result.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];
      if (sortKey === "timestamp") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [entries, search, filterAction, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / pageSize));
  const pagedEntries = filteredEntries.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const actionBadge = (action: string) => {
    const colors: Record<string, string> = {
      IpBlocked: "bg-red-100 text-red-700",
      IpUnblocked: "bg-green-100 text-green-700",
      IpBlockUpdated: "bg-blue-100 text-blue-700",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${colors[action] ?? "bg-slate-100 text-slate-700"}`}>
        {action === "IpBlocked" ? "Blocked" : action === "IpUnblocked" ? "Unblocked" : "Updated"}
      </span>
    );
  };

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  if (!subscription?.enableIpBlocking) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">IP Blocking Audit Log</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-amber-800">
            IP blocking is not enabled for your site.
          </p>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4">IP Blocking Audit Log</h2>
        <p className="text-slate-500">You do not have permission to view this audit log.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <h2 className="text-xl font-semibold mb-4">IP Blocking Audit Log</h2>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search details, performed by..."
          className="px-3 py-1.5 border border-slate-300 rounded text-sm w-64"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          aria-label="Search audit log"
        />
        <label htmlFor="filterAction" className="sr-only">Filter by action</label>
        <select
          id="filterAction"
          className="px-3 py-1.5 border border-slate-300 rounded text-sm"
          value={filterAction}
          onChange={(e) => { setFilterAction(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Actions</option>
          <option value="IpBlocked">Blocked</option>
          <option value="IpUnblocked">Unblocked</option>
          <option value="IpBlockUpdated">Updated</option>
        </select>
        <button
          className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          onClick={() => { setSearch(""); setFilterAction("all"); setCurrentPage(1); }}
        >
          Clear Filters
        </button>
        <span className="text-xs text-slate-400 ml-auto">
          {filteredEntries.length} of {entries.length} entries
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("timestamp")}>
                Date/Time{sortArrow("timestamp")}
              </th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("action")}>
                Action{sortArrow("action")}
              </th>
              <th className="px-3 py-2">Details</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("changedBy")}>
                Performed By{sortArrow("changedBy")}
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : pagedEntries.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No audit entries found</td></tr>
            ) : (
              pagedEntries.map((entry) => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-xs">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5">{actionBadge(entry.action)}</td>
                  <td className="px-3 py-1.5 text-xs max-w-[400px]">{entry.details}</td>
                  <td className="px-3 py-1.5 text-xs">{entry.changedBy}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
